import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Check, X, Search } from 'lucide-react';

/**
 * Unified, modular Select component supporting single/multi-select, search,
 * and custom option rendering.
 */
const Select = ({
  id,
  name,
  label,
  options = [],
  items, // alias for options (backward compatibility)
  value,
  selectedValue, // alias for value
  initialValue, // alias for value
  onChange,
  handleChange, // alias for onChange
  callback, // alias for onChange
  placeholder = 'Select an option',
  disabled = false,
  searchable = false,
  multiSelect = false,
  clearable = false,
  error,
  helperText,
  mandatory = false,
  native = false,
  className = '',
  selectClassName = '',
  size = 'md', // 'sm' | 'md' | 'lg'
}) => {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize options list
  const rawList = items || options || [];
  const normalizedOptions = useMemo(() => {
    return rawList.map((opt) => {
      if (typeof opt === 'string' || typeof opt === 'number') {
        return { value: opt, label: String(opt) };
      }
      return {
        value: opt?.value ?? opt?.id ?? opt?._id ?? opt?.val,
        label: opt?.label ?? opt?.name ?? opt?.title ?? String(opt?.value ?? ''),
        description: opt?.description,
        icon: opt?.icon,
        disabled: Boolean(opt?.disabled),
      };
    });
  }, [rawList]);

  // Current active value
  const currentValue = value ?? selectedValue ?? initialValue;

  // Internal selection state
  const [selected, setSelected] = useState(() => {
    if (multiSelect) {
      if (Array.isArray(currentValue)) return currentValue;
      return [];
    }
    return currentValue !== undefined && currentValue !== null ? currentValue : '';
  });

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropUp, setDropUp] = useState(false);

  // Auto-flip placement when opening near the bottom of viewport / modal
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // If less than 240px below and more room above, flip upwards
      setDropUp(spaceBelow < 240 && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  // Sync with prop changes
  useEffect(() => {
    if (multiSelect) {
      if (Array.isArray(currentValue)) {
        setSelected(currentValue);
      }
    } else if (currentValue !== undefined && currentValue !== null) {
      setSelected(currentValue);
    }
  }, [currentValue, multiSelect]);

  // Outside click listener
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Focus search input on open
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Notify parent handler
  const triggerChange = (val) => {
    const changeHandler = onChange || handleChange || callback;
    if (!changeHandler) return;

    if (callback) {
      // Dropdown legacy signature
      const selectedObj = normalizedOptions.find((o) => o.value === val);
      callback(selectedObj || val);
    } else {
      // Standard synthetic event signature
      changeHandler({
        target: {
          id: id || name,
          name: name || id,
          value: val,
        },
      });
    }
  };

  const handleSelectOption = (opt) => {
    if (opt.disabled) return;

    if (multiSelect) {
      const exists = Array.isArray(selected) && selected.includes(opt.value);
      const next = exists
        ? selected.filter((v) => v !== opt.value)
        : [...(Array.isArray(selected) ? selected : []), opt.value];

      setSelected(next);
      triggerChange(next);
    } else {
      setSelected(opt.value);
      triggerChange(opt.value);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    const next = multiSelect ? [] : '';
    setSelected(next);
    triggerChange(next);
  };

  const handleRemoveChip = (e, val) => {
    e.stopPropagation();
    const next = selected.filter((v) => v !== val);
    setSelected(next);
    triggerChange(next);
  };

  // Filtered options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return normalizedOptions;
    const q = searchQuery.toLowerCase();
    return normalizedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        (opt.description && opt.description.toLowerCase().includes(q))
    );
  }, [normalizedOptions, searchQuery]);

  // Find labels for selected value(s)
  const selectedLabel = useMemo(() => {
    if (multiSelect) return null;
    const match = normalizedOptions.find((opt) => opt.value === selected);
    return match ? match.label : '';
  }, [normalizedOptions, selected, multiSelect]);

  const sizeClasses = {
    sm: 'min-h-[34px] px-2.5 py-1 text-xs',
    md: 'min-h-[40px] px-3.5 py-2 text-sm',
    lg: 'min-h-[46px] px-4 py-2.5 text-base',
  };

  // NATIVE SELECT MODE
  if (native) {
    return (
      <div className={`flex flex-col ${className}`} ref={containerRef}>
        {label && (
          <label
            htmlFor={id}
            className="stride-form-label"
          >
            {label}
            {mandatory && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            id={id}
            name={name || id}
            value={selected}
            disabled={disabled}
            onChange={(e) => {
              setSelected(e.target.value);
              triggerChange(e.target.value);
            }}
            className={`
              w-full appearance-none bg-white border rounded-lg text-stride-body pr-10
              transition-colors duration-150 focus:outline-none focus:ring-2
              ${error ? 'border-red-400 focus:ring-red-100 focus:border-red-500' : 'border-border-subtle hover:border-slate-300 focus:ring-primary/20 focus:border-primary'}
              ${disabled ? 'bg-gray-100 text-stride-muted-light cursor-not-allowed border-border' : 'cursor-pointer'}
              ${sizeClasses[size] || sizeClasses.md}
              ${selectClassName}
            `}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {normalizedOptions.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-stride-muted">
            <ChevronDown size={16} />
          </div>
        </div>
        {error && <span className="stride-form-error">{error}</span>}
        {helperText && !error && (
          <span className="stride-form-helper">{helperText}</span>
        )}
      </div>
    );
  }

  // CUSTOM MODERN DROPDOWN MODE
  return (
    <div className={`flex flex-col relative ${className}`} ref={containerRef}>
      {label && (
        <label
          htmlFor={id}
          className="stride-form-label"
        >
          {label}
          {mandatory && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <div
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        className={`
          relative w-full flex items-center justify-between gap-2 bg-white border rounded-lg
          transition-all duration-150 text-left
          ${sizeClasses[size] || sizeClasses.md}
          ${error ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary'}
          ${isOpen ? 'border-primary ring-2 ring-primary/20 shadow-sm' : ''}
          ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-75' : 'cursor-pointer hover:border-gray-400'}
          ${selectClassName}
        `}
      >
        <div className="flex-1 flex flex-wrap items-center gap-1.5 overflow-hidden">
          {multiSelect ? (
            Array.isArray(selected) && selected.length > 0 ? (
              selected.map((val) => {
                const opt = normalizedOptions.find((o) => o.value === val);
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-soft text-primary text-xs font-medium"
                  >
                    {opt ? opt.label : val}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveChip(e, val)}
                      className="hover:text-red-600 focus:outline-none"
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })
            ) : (
              <span className="text-gray-400 text-sm select-none">{placeholder}</span>
            )
          ) : selectedLabel ? (
            <span className="text-gray-900 text-sm truncate">{selectedLabel}</span>
          ) : (
            <span className="text-gray-400 text-sm select-none">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0 text-gray-400">
          {clearable && ((multiSelect && selected?.length > 0) || (!multiSelect && selected)) && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:text-gray-600 focus:outline-none"
              title="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : ''}`}
          />
        </div>
      </div>

      {/* Floating Popover Options Menu */}
      {isOpen && (
        <div
          role="listbox"
          className={`stride-dropdown-menu left-0 right-0 ${dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}`}
        >
          {searchable && (
            <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
              <Search size={14} className="text-gray-400 shrink-0 ml-1" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none py-1"
                onClick={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          <div className="max-h-60 overflow-y-auto py-1.5">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-center text-xs text-gray-400">
                No options found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = multiSelect
                  ? Array.isArray(selected) && selected.includes(opt.value)
                  : selected === opt.value;

                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelectOption(opt)}
                    className={`
                      px-3.5 py-2 text-sm flex items-center justify-between gap-3 cursor-pointer
                      transition-colors duration-100
                      ${isSelected ? 'bg-primary-soft text-primary font-medium' : 'text-stride-body hover:bg-surface'}
                      ${opt.disabled ? 'opacity-40 cursor-not-allowed bg-gray-50' : ''}
                    `}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      {opt.icon && <span className="shrink-0">{opt.icon}</span>}
                      <div className="truncate">
                        <div className="truncate">{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-gray-400 font-normal truncate">
                            {opt.description}
                          </div>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <Check size={16} className="text-primary shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {error && <span className="stride-form-error">{error}</span>}
      {helperText && !error && (
        <span className="stride-form-helper">{helperText}</span>
      )}
    </div>
  );
};

export default Select;

